import { Add as AddIcon } from "@mui/icons-material";
import { Box, Grid, IconButton, Stack } from "@mui/material";
import { isNil } from "lodash";
import type { FC } from "react";
import { removeArr, updateArr } from "../../misc/utils";
import ProductOption from "./ProductOption";
import type { SelectedProductOption } from "./SelectedProductOption";
import { sortProductOptions } from "./sortProductOptions";

const AddOptionButton: FC<{ disabled: boolean; onClick: () => void }> = ({ disabled, onClick }) => (
  <IconButton aria-label="Dodaj opcję" disabled={disabled} onClick={onClick}>
    <AddIcon />
  </IconButton>
);

export interface ProductOptionType {
  readonly id: string;
  readonly name: string;
  readonly info?: string;
  readonly order?: number | null;
}

interface Props {
  readonly productId: string | undefined;
  readonly selectedOptions: SelectedProductOption[];
  readonly availableOptions: ProductOptionType[];
  readonly readonly?: boolean;
  readonly onOptionHintDoubleClick?: (option: SelectedProductOption) => void;
  readonly onChange: (options: SelectedProductOption[]) => void;
}

const ProductOptions: FC<Props> = ({
  productId,
  selectedOptions,
  availableOptions,
  readonly,
  onOptionHintDoubleClick,
  onChange,
}) => {
  const editDisabled = isNil(productId) || !!readonly;

  const updateOption = (optionIdx: number, update: SelectedProductOption) => {
    const option = selectedOptions[optionIdx];
    if (option && option.id !== update.id) {
      onChange(updateArr(selectedOptions, optionIdx, { ...update, quantity: 0 }));
    } else {
      onChange(
        updateArr(selectedOptions, optionIdx, {
          price: option?.price,
          ...update,
        }),
      );
    }
  };

  const sortedOptions = sortProductOptions(availableOptions);
  return (
    <Stack direction="row" spacing={1}>
      {!readonly && (
        <Box
          sx={{
            flexShrink: 0,
            flexGrow: 0,
          }}
        >
          <AddOptionButton
            disabled={editDisabled}
            onClick={() =>
              onChange([
                ...selectedOptions,
                {
                  id: undefined,
                  quantity: 0,
                },
              ])
            }
          />
        </Box>
      )}
      <Grid
        container
        sx={{
          flexGrow: 1,
        }}
      >
        {selectedOptions.map((option, optionIdx) => (
          <Grid key={optionIdx} size={{ xs: 12, lg: 6 }} container>
            <ProductOption
              value={option}
              readonly={editDisabled}
              options={sortedOptions.map((o) => ({
                label: o.name,
                value: o.id,
                tooltip: o.info,
              }))}
              hintText={option.hint}
              onHintDoubleClick={onOptionHintDoubleClick ? () => onOptionHintDoubleClick(option) : undefined}
              onChange={(o) => updateOption(optionIdx, o)}
              onDelete={() => onChange(removeArr(selectedOptions, optionIdx))}
            />
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
};

export default ProductOptions;
